import type { Address, Hex } from "viem";
import { MAX_UINT256 } from "@woodpecker/chain";
import { Y, type TextRef } from "../i18n";
import { useStore, type AuthRow } from "../store/store";
import { useWallet, type SignLine, type SignResult } from "../store/wallet";
import type { ConversationView } from "../adapters/clients";
import { ONE_WP, short } from "../lib/format";

/** Gas estimates for the fee line, in ETH (Base: a few gwei per transaction). */
export const GAS = {
  approve: 4e-6,
  authorize: 6e-6,
  authorizeMore: 2e-6,
  send: 3e-6,
  deployToken: 21e-6,
  setService: 3e-6,
  register: 5e-6,
  mint: 4e-6,
  exchange: 5e-6,
  claim: 5e-6,
  publish: 8e-6,
  unpublish: 3e-6,
};

export const authorizeText = (identity: string, token: string, cap: string | number) =>
  `authorize(${short(identity)}, ${token}, ${cap})`;
export const capsText = (caps: Record<string, number | string>) =>
  Object.entries(caps)
    .map(([t, n]) => `${n} ${t}`)
    .join(" · ");

/** The lines of the wallet's writes, each bound to a real transaction through the chain client. */
export function useSignLines() {
  const { chain, wallet, config, tokens } = useStore();
  const w = () => {
    if (!wallet) throw new Error("no wallet");
    return wallet.address;
  };
  const addr = (symbol: string): Address => {
    const t = tokens.get(symbol);
    if (!t) throw new Error(`unknown token ${symbol}`);
    return t.address;
  };
  const rawCap = (symbol: string, cap: number): bigint =>
    symbol === "wpCredit" ? BigInt(Math.round(cap * 1e6)) : BigInt(Math.floor(cap));
  return {
    approve: (symbol: string, spender: Address = config.contractA, plain?: TextRef): SignLine => ({
      plain: plain ?? Y("wallet.plain.approve", { token: symbol }),
      text: `approve ${symbol} → ${spender === config.contractA ? "ServiceAuthorization" : short(spender)}`,
      address: addr(symbol),
      gas: GAS.approve,
      run: async () => ({ tx: (await chain.approve(w(), addr(symbol), spender, MAX_UINT256)).tx }),
    }),
    authorize: (plain: TextRef, identity: Address, caps: Record<string, number>): SignLine => {
      const entries = Object.entries(caps);
      const calls = entries.map(([tok, cap]) => authorizeText(identity, tok, cap));
      if (entries.length === 1) {
        const [tok, cap] = entries[0]!;
        return {
          plain,
          text: calls[0]!,
          address: identity,
          gas: GAS.authorize,
          run: async () => ({ tx: (await chain.authorize(w(), identity, addr(tok), rawCap(tok, cap))).tx }),
        };
      }
      return {
        plain,
        text: `multicall(authorize ×${entries.length})`,
        calls,
        address: identity,
        gas: GAS.authorize + (entries.length - 1) * GAS.authorizeMore,
        run: async () => ({
          tx: (
            await chain.multicallAuthorize(
              w(),
              entries.map(([tok, cap]) => ({ identity, token: addr(tok), cap: rawCap(tok, cap) })),
            )
          ).tx,
        }),
      };
    },
    exchange: (symbol: string, credits: number, owner: string): SignLine => ({
      plain: Y("wallet.plain.exchange", {
        credits,
        owner,
        tokens: Math.floor((credits * 1e6) / Number(tokens.get(symbol)?.rate ?? 1n)),
        token: symbol,
      }),
      text: `${symbol}.exchange(${Math.round(credits * 1e6)}, ${short(w())})`,
      address: addr(symbol),
      gas: GAS.exchange,
      run: async () => ({ tx: (await chain.exchange(w(), addr(symbol), BigInt(Math.round(credits * 1e6)), w())).tx }),
    }),
    setRate: (symbol: string, perCredit: number): SignLine => {
      const rate = BigInt(Math.round(1e6 / perCredit));
      return {
        plain: Y("wallet.plain.setRate", { n: perCredit, token: symbol }),
        text: `${symbol}.setRate(${rate})`,
        address: addr(symbol),
        gas: GAS.setService,
        run: async () => ({ tx: (await chain.setRate(w(), addr(symbol), rate)).tx }),
      };
    },
    mint: (symbol: string, to: Address, amount: number, plain: TextRef): SignLine => ({
      plain,
      text: `mint ${amount} ${symbol} → ${short(to)}`,
      address: addr(symbol),
      gas: GAS.mint,
      run: async () => ({ tx: (await chain.mint(w(), addr(symbol), to, BigInt(amount))).tx }),
    }),
    sendEth: (to: Address, eth: string, name: string): SignLine => ({
      plain: Y("wallet.plain.fund", { amount: eth, name }),
      text: `send ${eth} ETH → ${short(to)}`,
      address: to,
      gas: GAS.send,
      run: async () => ({ tx: (await chain.sendEth(w(), to, BigInt(Math.round(Number(eth) * 1e18)))).tx }),
    }),
    /** The wallet claims its handle in the ServiceDirectory, once (D42): the first publish of a wallet without one. */
    claim: (name: string): SignLine => ({
      plain: Y("wallet.plain.claim", { handle: `${name}@${w().slice(2, 6).toLowerCase()}` }),
      text: `ServiceDirectory.claim("${name}")`,
      address: config.directory,
      gas: GAS.claim,
      run: async () => ({ tx: (await chain.claim(w(), name)).tx }),
    }),
    /** The wallet publishes a version of its extension: the url the host serves it from and the hash of its manifest (D70). */
    publish: (id: string, version: string, name: string, url: string, hash: Hex): SignLine => ({
      plain: Y("wallet.plain.publish", { id, version }),
      text: `ExtensionDirectory.publish("${name}", "${url}", ${hash.slice(0, 10)}…)`,
      address: config.extensionDirectory,
      gas: GAS.publish,
      run: async () => ({ tx: (await chain.publish(w(), name, url, hash)).tx }),
    }),
    unpublish: (id: string, name: string): SignLine => ({
      plain: Y("wallet.plain.unpublish", { id }),
      text: `ExtensionDirectory.unpublish("${name}")`,
      address: config.extensionDirectory,
      gas: GAS.unpublish,
      run: async () => ({ tx: (await chain.unpublish(w(), name)).tx }),
    }),
    raw: (line: Omit<SignLine, "run">, run: SignLine["run"]): SignLine => ({ ...line, run }),
  };
}

/** Stop on chain: cap = used on every open record (D40). */
export function useStopOnChain() {
  const { refreshAuth } = useStore();
  const { request } = useWallet();
  const lines = useSignLines();
  return (c: ConversationView, rows: AuthRow[], onDone?: (r: SignResult) => void) => {
    const used = Object.fromEntries(rows.map((r) => [r.token, r.used]));
    request({
      title: Y("wallet.req.stopChain", { title: c.title || Y("conv.untitled") }),
      note: Y("wallet.req.stopNote", { n: rows.length }),
      lines: [
        lines.authorize(
          Y("wallet.plain.stopChain", { title: c.title || Y("conv.untitled"), caps: capsText(used) }),
          c.agent,
          used,
        ),
      ],
      onDone: (r) => {
        if (r === "signed") void refreshAuth(c.id, c.agent);
        onDone?.(r);
      },
    });
  };
}

export { ONE_WP };
